import { useState } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const Methodology = () => {
  const [expandedCards, setExpandedCards] = useState({})

  const toggleCard = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }
  const steps = [
    {
      title: 'Research',
      description: 'Understand the core issue and contextualising the brief within the given opportunities and challenges.',
      detail: "It's been always a strong focus of mine to understand the market opportunity and added value for the final application early on."
    },
    {
      title: 'Ideation',
      description: 'A hypothesis for the future solution that allows to align on a common ambition.',
      detail: 'During this phase it is very important to align on subtle expectations around the project and to generate a visionary drive for everyone involved. Usually with the help of sketches and solution statements alike.'
    },
    {
      title: 'Prototype',
      description: 'Bring ideas to life quickly through tangible models, mockups, or simulations.',
      detail: 'This step turns abstract concepts into testable artifacts - allowing for early feedback, iteration, and learning by making. The quicker it\'s real the better the learning. This can be achieved with various levels of fidelity.'
    },
    {
      title: 'Refinement',
      description: 'Finally iterative prototyping and hypothesising needs to funnel down into the best possible solution.',
      detail: 'Through testing, validation, and analysis, this phase filters the viable from the flawed - preparing concepts for real-world application or further iteration. Usually involving CAD iterations and 0 series production prototypes.'
    }
  ]

  return (
    <>
      {/* Sustainability First Section */}
      <section className="py-[120px] px-0 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-[1400px] mx-auto px-[5%]"
        >
          <h2 className="text-2xl sm:text-3xl md:text-[clamp(2rem,4vw,3.5rem)] font-semibold mb-6 sm:mb-8 tracking-tight text-center">
            Sustainability First Approach
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-base sm:text-lg md:text-[clamp(1.125rem,2vw,1.5rem)] text-muted-foreground leading-relaxed px-4 sm:px-0">
              A fundamental principle guides every project from conception to completion. By prioritizing environmental responsibility, resource efficiency, and long-term impact, design solutions not only meet immediate needs but also contribute positively to our shared future.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Methodology Steps */}
      <section className="py-[120px] px-0 bg-secondary">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-[1400px] mx-auto px-[5%]"
        >
          <h2 id="methodology" data-section="methodology" className="text-xl font-semibold mb-16 tracking-tight">
            Design Process
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <Card className="shadow-md hover:shadow-lg transition-shadow h-full">
                  <CardHeader>
                    <CardTitle className="mb-2">{step.title}</CardTitle>
                    <hr className="border-border/50 my-4" />
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base mb-4">
                      {step.description}
                    </CardDescription>
                    {expandedCards[index] && (
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {step.detail}
                      </p>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleCard(index)}
                      className="rounded-full"
                    >
                      {expandedCards[index] ? 'Less' : 'More'}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* AI Co-Creation Section */}
      <section className="py-[120px] px-0 bg-background">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-[1400px] mx-auto px-[5%]"
        >
          <h2 className="text-2xl sm:text-3xl md:text-[clamp(2rem,4vw,3.5rem)] font-semibold mb-6 sm:mb-8 tracking-tight text-center">
            Curated Co-Creation with AI
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-base sm:text-lg md:text-[clamp(1.125rem,2vw,1.5rem)] text-muted-foreground leading-relaxed px-4 sm:px-0">
              Embracing the collaborative potential of artificial intelligence as a creative partner. Through symbiotic interplay, AI enhances the design process by expanding possibilities, accelerating iteration, and uncovering innovative solutions that arise from the intersection of human intuition and machine intelligence. This co-creation approach enables the exploration of complex design spaces more comprehensively while preserving the essential human touch in decision-making and creative vision.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Gantt Chart Example Section - Dark Mode */}
      <section className="py-[120px] px-0 bg-black text-white dark:bg-black dark:text-white dark-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-[1400px] mx-auto px-[5%]"
        >
          <h2 className="text-lg sm:text-xl font-semibold mb-6 sm:mb-8 tracking-tight text-white">
            Real-Time Project Timeline
          </h2>
          <div className="bg-white/10 rounded-lg p-4 sm:p-6 md:p-8 shadow-lg border border-white/10">
            <div className="space-y-4">
              {/* Gantt Chart Visual Example */}
              <div className="relative overflow-hidden">
                <div className="overflow-x-auto -mx-4 sm:mx-0 scrollbar-dark">
                  <div className="min-w-[500px] sm:min-w-[600px] space-y-3 px-4 sm:px-0">
                  {/* Timeline Header */}
                  <div className="flex gap-2 pb-2 border-b border-white/20">
                    <div className="w-40 text-sm text-white/70 font-medium">Task</div>
                    <div className="flex-1 flex gap-2">
                      <div className="flex-1 text-xs text-center text-white/70">Week 1</div>
                      <div className="flex-1 text-xs text-center text-white/70">Week 2</div>
                      <div className="flex-1 text-xs text-center text-white/70">Week 3</div>
                      <div className="flex-1 text-xs text-center text-white/70">Week 4</div>
                      <div className="flex-1 text-xs text-center text-white/70">Week 5</div>
                      <div className="flex-1 text-xs text-center text-white/70">Week 6</div>
                    </div>
                  </div>
                  
                  {/* Task Bars */}
                  <div className="flex gap-2 items-center">
                    <div className="w-40 text-sm text-white">Research</div>
                    <div className="flex-1 flex gap-2 relative h-8">
                      <div className="absolute left-0 w-[20%] h-full bg-green-500/70 rounded-l flex items-center justify-center">
                        <span className="text-xs text-white font-medium">Done</span>
                      </div>
                      <div className="absolute left-[20%] w-[13%] h-full bg-blue-500/50 rounded-r flex items-center justify-center">
                        <span className="text-xs text-white font-medium">In Progress</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 items-center">
                    <div className="w-40 text-sm text-white">Ideation</div>
                    <div className="flex-1 flex gap-2 relative h-8">
                      <div className="absolute left-[25%] w-[25%] h-full bg-white/20 rounded flex items-center justify-center">
                        <span className="text-xs text-white/80">Planned</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 items-center">
                    <div className="w-40 text-sm text-white">Prototyping</div>
                    <div className="flex-1 flex gap-2 relative h-8">
                      <div className="absolute left-[50%] w-[33%] h-full bg-white/20 rounded flex items-center justify-center">
                        <span className="text-xs text-white/80">Planned</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 items-center">
                    <div className="w-40 text-sm text-white">Refinement</div>
                    <div className="flex-1 flex gap-2 relative h-8">
                      <div className="absolute left-[83%] w-[17%] h-full bg-white/20 rounded flex items-center justify-center">
                        <span className="text-xs text-white/80">Planned</span>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </div>
              
              <p className="text-sm text-white/80 mt-6">
                Visualize project progress with interactive Gantt charts that keep all stakeholders aligned and informed.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Fluid Client Connection Section - Dark Mode */}
      <section className="py-[120px] px-0 bg-black text-white dark:bg-black dark:text-white dark-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-[1400px] mx-auto px-[5%]"
        >
          <h2 className="text-2xl sm:text-3xl md:text-[clamp(2rem,4vw,3.5rem)] font-semibold mb-6 sm:mb-8 tracking-tight text-center text-white">
            Fluid Client Connection
          </h2>
          <div className="prose prose-lg max-w-none">
            <p className="text-base sm:text-lg md:text-[clamp(1.125rem,2vw,1.5rem)] text-white/80 leading-relaxed px-4 sm:px-0">
              This platform enables a seamless, continuous connection between clients and collaborators. Stay up-to-date on project progress, timelines, and developments in real-time. Through this integrated system, all stakeholders remain informed and engaged throughout the entire design process, fostering transparency and enabling meaningful collaboration that extends beyond traditional project boundaries.
            </p>
          </div>
        </motion.div>
      </section>
    </>
  )
}

export default Methodology

